import React, { useState, useEffect, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import * as api from 'config/api';
import * as useAPI from 'hooks/useAPI';
import { useModal } from 'hooks/useModal';
import { formatNumber } from 'utils/formatter';
import { UserTags } from 'pages/PersonaChart';
import Icon from 'components/commons/icon/Icon';
import { rangeSelectorButtonAlignLeftChartClassName } from 'components/features/home/assetStuffing/AssetStuffingChart';
import {
  getUnit as getFormatUnit,
  floorByUnit,
} from 'components/features/new-report/utils/constructor';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import { PersonaChartFilterEditModal } from './PersonaChartFilterEditModal';
import { WhatIsPersonaModal } from './WhatIsPersonaModal';
import {
  ChartType,
  ContentLoading,
  assetFilterArr,
} from './PersonaChartAssetTransition';
import { UserTag, getColor, getInfo, getUnit } from '../utils/personaChart';

type ScopeDropDownType = 1 | 2 | 3 | 4;

type ScopeDropDownList = {
  type: ScopeDropDownType;
  label: string;
};

type Distributions = {
  rangeFrom: number;
  rangeTo: number;
  userRatio: number;
  userFlag: boolean;
  targetFlag: boolean;
};

type AssetDistributionData = {
  totalAssetAmount: number;
  medianTotalAssetAmount: number | null;
  distributions: Distributions[];
};

type CheckedRange = {
  '50MM': boolean;
  '100MM': boolean;
  '500MM': boolean;
};

export const PersonaChartAssetDistribution = ({
  userTags,
  user,
  type,
}: {
  userTags: UserTags;
  user: api.User;
  type: ChartType;
}): React.ReactElement => {
  const [
    assetDistributionData,
    setAssetDistributionData,
  ] = useState<AssetDistributionData | null>(null);

  const [state, setState] = useState<
    useAPI.State<'state', api.GetPersonaAssetDistributionResult>
  >(useAPI.loading);

  const initialSelectedAssetsFilter = assetFilterArr(
    userTags,
    type
  ) as UserTag[];
  const [selectedAssetsFilter, setSelectedAssetsFilter] = useState<UserTag[]>(
    initialSelectedAssetsFilter
  );

  const assetTag = userTags.assetTag;
  const initialSelectedScopeFilter =
    assetTag <= 1209 ? 1 : assetTag <= 1219 ? 2 : assetTag <= 1229 ? 3 : 4;
  const [selectedScopeFilter, setSelectedScopeFilter] = useState<number>(
    initialSelectedScopeFilter
  );

  const [isCheckedRange, setIsCheckedRange] = useState<CheckedRange>({
    '50MM': false,
    '100MM': false,
    '500MM': false,
  });

  useEffect(() => {
    const ageTag = selectedAssetsFilter.find(
      num => num !== null && 1101 <= num && num <= 1110
    );
    const annualIncomeTag = selectedAssetsFilter.find(
      num => num !== null && 1300 <= num && num <= 1400
    );
    const investmentStartYearTag = selectedAssetsFilter.find(
      num => num !== null && 1500 <= num && num <= 1506
    );
    const investmentProductsTag = selectedAssetsFilter.filter(
      num => num !== null && 2101 <= num && num <= 2202
    );

    const queryTagsList = [
      annualIncomeTag,
      investmentStartYearTag,
      ...investmentProductsTag,
    ].filter(v => v) as number[];

    let maxAssetM = null;
    if (selectedScopeFilter === 1) {
      maxAssetM = 10;
      setIsCheckedRange({ '50MM': false, '100MM': false, '500MM': false });
    } else if (selectedScopeFilter === 2) {
      maxAssetM = 50;
      setIsCheckedRange({ '50MM': true, '100MM': false, '500MM': false });
    } else if (selectedScopeFilter === 3) {
      maxAssetM = 100;
      setIsCheckedRange({ '50MM': true, '100MM': true, '500MM': false });
    } else {
      setIsCheckedRange({ '50MM': true, '100MM': true, '500MM': true });
    }

    setState(useAPI.loading);
    api
      .getPersonaAssetDistribution({
        ...(maxAssetM !== null ? { MaxAssetM: maxAssetM } : {}),
        ...(ageTag !== undefined ? { AgeTag: ageTag } : {}),
        ...(queryTagsList !== undefined ? { QueryTags: queryTagsList } : {}),
      })
      .then(ret => {
        const result = ret as api.GetPersonaAssetDistributionResult;
        const distributionData = result.Distributions.map(ret => {
          return {
            rangeFrom: ret.RangeFrom,
            rangeTo: ret.RangeTo,
            userRatio: ret.UserRatio,
            userFlag: ret.UserFlag,
            targetFlag: ret.TargetFlag,
          };
        }) as Distributions[];

        const list = {
          totalAssetAmount: result.TotalAssetAmount,
          medianTotalAssetAmount: result.MedianTotalAssetAmount,
          distributions: distributionData,
        } as AssetDistributionData;
        setAssetDistributionData(list);
        setState(useAPI.loaded({ state: result }));
      });
  }, [selectedAssetsFilter, selectedScopeFilter]);

  const [
    isShowPersonaChartFilterEditModal,
    {
      hideModal: hidePersonaChartFilterEditModal,
      showModal: showPersonaChartFilterEditModal,
    },
  ] = useModal();

  const [
    isShowWhaiIsPersonModal,
    { hideModal: hideWhaiIsPersonModal, showModal: showWhaiIsPersonModal },
  ] = useModal();

  const updateAssetsFilter = (tag: UserTag) => {
    const updFilter = selectedAssetsFilter.filter(list => list !== tag);
    setSelectedAssetsFilter(updFilter);
  };

  if (assetDistributionData === null || useAPI.isLoading(state)) {
    return <ContentLoading title="保有資産を比べてみる" height="660px" />;
  }

  const assetsDifference =
    assetDistributionData.medianTotalAssetAmount === null
      ? null
      : assetDistributionData.totalAssetAmount -
        assetDistributionData.medianTotalAssetAmount;
  const isAboveAverage =
    assetsDifference === null ? null : assetsDifference > 0;

  return (
    <>
      <div className="pt-8 font-bold">保有資産を比べてみる</div>
      <div className="flex flex-row items-center mt-2 pt-4 pb-2 px-4 border border-bluegray-300 rounded-t-lg">
        <div>
          <img
            src="/static/images/persona/persona_icon.svg"
            className=""
            alt="アイコン"
          />
        </div>
        <div className="ml-2 w-full">
          <div className="flex flex-row items-center text-xs">
            <div
              className="flex flex-row items-center cursor-pointer"
              onClick={showWhaiIsPersonModal}>
              <div className="text-bluegray-500">この特徴のグループと比較</div>
              <div className="icon-help_outline ml-1 text-sm text-bluegray-500"></div>
            </div>
            <div
              className="ml-auto text-blue-700 cursor-pointer"
              onClick={showPersonaChartFilterEditModal}>
              設定変更
            </div>
          </div>
          <div className="flex flex-row flex-wrap">
            {selectedAssetsFilter.length === 0 ? (
              <div className="mt-1 text-bluegray-500 text-xs">
                絞り込み条件なし
              </div>
            ) : (
              selectedAssetsFilter.map((tag, key) => {
                if (tag === null) return <></>;
                const text =
                  1108 <= tag && tag <= 1110
                    ? '80代以上'
                    : getInfo(tag, true) + getUnit(tag);
                const color = getColor(tag);
                return (
                  <>
                    <div
                      className={`flex flex-row items-center px-1 py-0.5 mt-2 mr-2 border ${
                        color !== null
                          ? `${color}`
                          : 'bg-bluegray-100 border-bluegray-300'
                      } rounded cursor-pointer`}
                      onClick={() => updateAssetsFilter(tag)}
                      key={key}>
                      <div className="text-xs">{text}</div>
                      <div className="ml-2">
                        <Icon className="text-sm" name="close" />
                      </div>
                    </div>
                  </>
                );
              })
            )}
          </div>
        </div>
      </div>
      <div className="border-b border-r border-l border-bluegray-300">
        <div className="pt-2 px-2">
          <Chart
            {...{
              assetDistributionData: assetDistributionData.distributions,
              selectedScopeFilter,
              setSelectedScopeFilter,
              isCheckedRange,
              setIsCheckedRange,
            }}
          />
        </div>
      </div>
      <div className="px-4 pt-4 border-b border-r border-l border-bluegray-300 rounded-b-lg bg-bluegray-700">
        <div className="flex flex-row">
          <div className="w-1/2 text-center text-white border-r border-gray-300">
            <div className="flex justify-center">
              <img
                src="/static/images/persona/persona_icon.svg"
                alt="アイコン"
                style={{ width: '32px', height: '32px' }}
              />
            </div>
            <div className="text-lg font-bold">
              {(num => {
                const unit = num === null ? '' : getFormatUnit(num);
                return (
                  <>
                    {num === null ? '---' : commaFormat(floorByUnit(num, unit))}
                    <span className="text-sm">{unit}円</span>
                  </>
                );
              })(assetDistributionData.medianTotalAssetAmount)}
            </div>
            <div className="flex flex-row items-center justify-center">
              <div className="">
                <img
                  src="/static/images/persona/legend1.svg"
                  className=""
                  alt="凡例"
                />
              </div>
              <div className="ml-1 text-1.5xs">グループ平均の資産総額</div>
            </div>
          </div>
          <div className="w-1/2 text-center text-white">
            <div className="flex justify-center">
              {user.ProfileImageURL ? (
                <img
                  src={user.ProfileImageURL}
                  className="rounded-full"
                  alt="プロフィール"
                  style={{ width: '32px', height: '32px' }}
                />
              ) : (
                <div
                  className="icon-person text-3xl border rounded-full border-gray-700 flex justify-center items-center"
                  style={{ width: '32px', height: '32px' }}></div>
              )}
            </div>
            <div className="text-lg font-bold">
              {(num => {
                const unit = getFormatUnit(num);
                return (
                  <>
                    {commaFormat(floorByUnit(num, unit))}
                    <span className="text-sm">{unit}円</span>
                  </>
                );
              })(assetDistributionData.totalAssetAmount)}
            </div>
            <div className="flex flex-row items-center justify-center">
              <div className="">
                <img
                  src="/static/images/persona/legend2.svg"
                  className=""
                  alt="凡例"
                />
              </div>
              <div className="ml-1 text-1.5xs">あなたの資産総額</div>
            </div>
          </div>
        </div>
        <div
          className="grid mt-4 py-4 px-4 rounded-t-lg bg-white"
          style={{ gridTemplateColumns: 'auto 1fr' }}>
          <div className="flex items-center">
            <img
              src={`/static/images/persona/persona_image${
                isAboveAverage || assetsDifference === 0 ? 1 : 4
              }.svg`}
              className=""
              style={{ width: '60px', height: '60px' }}
              alt="アイコン"
            />
          </div>
          <div className="ml-2 text-sm">
            {assetsDifference === null ? (
              <>
                <div className="font-bold">比較対象がいません</div>
                <div>グループの設定を変更してみましょう。</div>
              </>
            ) : assetsDifference === 0 ? (
              <>
                <div className="font-bold">
                  グループ平均と変わりありません。
                </div>
                <div>
                  あなたの資産は、グループの平均と変わらないようです。
                  <br />
                  上手に資産形成できていますね！
                </div>
              </>
            ) : (
              <>
                <div className="font-bold">
                  グループ平均を
                  {isAboveAverage ? (
                    <span className="text-red-600">上回って</span>
                  ) : (
                    <span className="text-cyan-500">下回って</span>
                  )}
                  います
                </div>
                <div>
                  あなたの資産は、グループの平均より約
                  {(num => {
                    const unit = getFormatUnit(num);
                    return `${commaFormat(floorByUnit(num, unit))}${unit}`;
                  })(assetsDifference)}
                  円ほど{isAboveAverage ? '高い' : '低い'}ようです。
                  <br />
                  {isAboveAverage
                    ? '上手に資産形成できていますね！'
                    : '資産形成を継続していきましょう。'}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <PersonaChartFilterEditModal
        hide={hidePersonaChartFilterEditModal}
        isShow={isShowPersonaChartFilterEditModal}
        chartAssetFilter={selectedAssetsFilter}
        setChartAssetFilter={setSelectedAssetsFilter}
        type="assetDistribution"
      />
      <WhatIsPersonaModal
        hide={hideWhaiIsPersonModal}
        isShow={isShowWhaiIsPersonModal}
      />
    </>
  );
};

const Chart = ({
  assetDistributionData,
  selectedScopeFilter,
  setSelectedScopeFilter,
  isCheckedRange,
  setIsCheckedRange,
}: {
  assetDistributionData: Distributions[];
  selectedScopeFilter: number;
  setSelectedScopeFilter: React.Dispatch<React.SetStateAction<number>>;
  isCheckedRange: CheckedRange;
  setIsCheckedRange: React.Dispatch<React.SetStateAction<CheckedRange>>;
}): React.ReactElement => {
  const selectedPeriodFilterList = [
    {
      type: 1,
      label: '～1,000万円',
    },
    {
      type: 2,
      label: '～5,000万円',
    },
    {
      type: 3,
      label: '～1億円',
    },
    {
      type: 4,
      label: '～5億円以上',
    },
  ] as ScopeDropDownList[];

  const selectedScopeFilterStates = selectedPeriodFilterList.map(
    list => list.type
  );
  const selectedScopeFilterLabel = (type: number) => {
    const info = selectedPeriodFilterList.find(list => type === list.type);
    return info === undefined ? '' : info.label;
  };

  const toString = (value: number) => `${value.toString()}`;
  const fromString = (str: string) => {
    const num = Number(str);
    return num as number;
  };

  const [options, setOptions] = useState<Highcharts.Options>({
    chart: {
      className: rangeSelectorButtonAlignLeftChartClassName,
      animation: {
        duration: 0,
      },
      height: 210,
      marginTop: 40,
      marginBottom: 20,
      backgroundColor: '#ffffff',
    },
    credits: {
      enabled: false,
    },
    exporting: { enabled: false },
    rangeSelector: {
      enabled: false,
      dropdown: 'always',
      x: 20,
      inputEnabled: false,
    },
    yAxis: {
      allowDecimals: true,
      labels: {
        align: 'left',
        x: -10,
        y: 4,
        formatter(): string {
          return `${this.value}%`;
        },
      },
      title: {
        text: null,
      },
      opposite: false,
    },
    tooltip: {
      borderColor: 'transparent',
      useHTML: true,
      outside: true,
      shared: true,
      formatter: function() {
        return renderToStaticMarkup(
          <Tooltip>
            {(this.points ?? []).map(point => (
              <TooltipItem
                asset={point.x}
                name={point.series.name}
                value={point.y}
                type={selectedScopeFilter}
              />
            ))}
          </Tooltip>
        );
      },
    },
    plotOptions: {
      series: {
        turboThreshold: 15000,
      },
      column: {
        borderRadius: 3,
      },
    },
    navigator: {
      enabled: false,
      maskFill: 'rgba(203, 213, 225, 0.6)',
    },
    title: {
      text: '',
      style: { display: 'none' },
    },
    legend: {
      enabled: false,
    },
  });

  useEffect(() => {
    const tickInterval =
      selectedScopeFilter === 1
        ? 200
        : selectedScopeFilter === 2
        ? 1000
        : selectedScopeFilter === 3
        ? 2000
        : 10000;
    setOptions(prev => ({
      ...prev,
      xAxis: {
        minPadding: 0.04,
        gridLineWidth: 0,
        title: { style: { color: '#000000' } },
        labels: {
          formatter(): string {
            return `${this.value}`;
          },
          style: { color: '#000000' },
        },
        tickLength: 0,
        tickInterval: tickInterval,
      },
      series: [
        {
          type: 'column',
          name: '全体の割合',
          data: chartDataFormat(assetDistributionData),
          events: {
            legendItemClick: (): false => false,
          },
          states: {
            hover: {
              enabled: false,
            },
          },
        },
      ],
      tooltip: {
        borderColor: 'transparent',
        useHTML: true,
        outside: true,
        shared: true,
        formatter: function() {
          return renderToStaticMarkup(
            <Tooltip>
              {(this.points ?? []).map(point => (
                <TooltipItem
                  asset={point.x}
                  name={point.series.name}
                  value={point.y}
                  type={selectedScopeFilter}
                />
              ))}
            </Tooltip>
          );
        },
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetDistributionData]);

  const check10MM = () => {
    setIsCheckedRange({ '50MM': false, '100MM': false, '500MM': false });
    setSelectedScopeFilter(1);
  };

  const check50MM = () => {
    setIsCheckedRange({ '50MM': true, '100MM': false, '500MM': false });
    setSelectedScopeFilter(2);
  };

  const check100MM = () => {
    setIsCheckedRange({ '50MM': true, '100MM': true, '500MM': false });
    setSelectedScopeFilter(3);
  };

  const check500MM = () => {
    setIsCheckedRange({ '50MM': true, '100MM': true, '500MM': true });
    setSelectedScopeFilter(4);
  };

  const ref = useRef<HighchartsReact.RefObject>(null);
  return (
    <>
      <div className="flex flex-row items-center justify-center mt-2 text-1.5xs">
        <div className="ml-2">全体の割合</div>
        <div className="ml-auto">
          <img
            src="/static/images/persona/legend2.svg"
            className=""
            alt="凡例"
          />
        </div>
        <div className="ml-1">あなた</div>
        <div className="ml-2">
          <img
            src="/static/images/persona/legend1.svg"
            className=""
            alt="凡例"
          />
        </div>
        <div className="ml-1 mr-3">グループ平均</div>
      </div>
      <div className="ml-2">
        <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
      </div>
      <div className="flex flex-row my-2 mx-2 text-1.5xs">
        <div className="ml-auto text-bluegray-500">単位：万円</div>
      </div>
      <div className="w-full h-full relative mb-4">
        <div
          className="grid w-full mt-4 mb-2 assets-range cursor-pointer"
          style={{ gridTemplateColumns: '1fr 3fr 3fr 4fr 5fr' }}>
          <div className="flex flex-col items-center cursor-pointer">
            <div className="circle-check z-10" />
          </div>
          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={check10MM}>
            <div
              className="percentage absolute bg-orange-200 cursor-pointer"
              style={{
                left: '3.9%',
                top: '13.5%',
                width: '11%',
                zIndex: 1,
              }}></div>
            <div className="circle-check z-10" />
            <label className="text-1.5xs mt-1">～1,000万円</label>
          </div>
          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={check50MM}>
            <div
              className={`percentage absolute ${
                isCheckedRange['50MM'] ? 'bg-orange-200' : 'bg-gray-250'
              }`}
              style={{
                left: '16.5%',
                top: '13.5%',
                width: '17.5%',
                zIndex: 1,
              }}></div>
            <div
              className={`z-10 ${
                isCheckedRange['50MM'] === true ? 'circle-check' : 'circle'
              }`}
            />
            <label className="text-1.5xs mt-1">～5,000万円</label>
          </div>
          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={check100MM}>
            <div
              className={`percentage absolute ${
                isCheckedRange['100MM'] ? 'bg-orange-200' : 'bg-gray-250'
              }`}
              style={{
                left: '35%',
                top: '13.5%',
                width: '21%',
                zIndex: 1,
              }}></div>
            <div
              className={`z-10 ${
                isCheckedRange['100MM'] === true ? 'circle-check' : 'circle'
              }`}
            />
            <label className="text-1.5xs mt-1">～1億円</label>
          </div>
          <div
            className="flex flex-col items-center ml-auto mr-2"
            onClick={check500MM}>
            <div
              className={`percentage absolute ${
                isCheckedRange['500MM'] ? 'bg-orange-200' : 'bg-gray-250'
              }`}
              style={{
                left: '57%',
                top: '13.5%',
                width: '43%',
                zIndex: 1,
              }}></div>
            <div
              className={`z-10 ${
                isCheckedRange['500MM'] === true ? 'circle-check' : 'circle'
              }`}
            />
            <label className="text-1.5xs mt-1">5億円以上</label>
          </div>
        </div>
      </div>
      <div
        className={`relative flex flex-row overflow-hidden w-full items-center rounded border h-8 mt-3 mb-4 ml-2 border-bluegray-400`}
        style={{ width: '150px' }}>
        <select
          className={`w-full h-full appearance-none bg-transparent text-xs pl-3 cursor-pointer`}
          value={selectedScopeFilter}
          onChange={(e): void => {
            const result = fromString(e.currentTarget.value);
            setSelectedScopeFilter(result);
          }}>
          {selectedScopeFilterStates.map((s, i) => {
            return (
              <option key={i} value={toString(s)}>
                {selectedScopeFilterLabel(s)}
              </option>
            );
          })}
        </select>
        <div
          className={'absolute pointer-events-none flex items-center pr-1'}
          style={{ right: '6%' }}>
          <i
            className={`text-bluegray-400 cursor-pointer icon-chevron-down p-0.5 ml-auto`}
          />
        </div>
      </div>
    </>
  );
};

const chartDataFormat = (data: Distributions[]) => {
  return data.map(
    (data): Highcharts.PointOptionsObject => {
      return {
        x: data.rangeFrom / 10000,
        y: data.userRatio,
        color: data.userFlag ? '#FB923C' : '#E2E8F0',
        dataLabels:
          data.userFlag || data.targetFlag
            ? [
                {
                  formatter(): string {
                    return renderToStaticMarkup(
                      <TargetAchieveLabel
                        userFlag={data.userFlag}
                        targetFlag={data.targetFlag}
                      />
                    );
                  },
                  allowOverlap: true,
                  overflow: 'allow',
                  crop: false,
                  enabled: true,
                  useHTML: true,
                },
              ]
            : [],
      };
    }
  );
};

const TargetAchieveLabel = ({
  userFlag,
  targetFlag,
}: {
  userFlag: boolean;
  targetFlag: boolean;
}): React.ReactElement => {
  return (
    <>
      {targetFlag && (
        <div
          className="rounded-full mb-0.5 text-white relative"
          style={{ backgroundColor: '#14B8A6', padding: '0.1rem' }}>
          <div
            className="absolute w-0 h-0 bottom-0 right-1/2"
            style={{
              transform: 'translate(50%,90%)',
              borderStyle: 'solid',
              borderWidth: '8px 2px 0 2px',
              borderColor: '#14B8A6 transparent transparent transparent',
            }}></div>
          <div className="icon-person text-sm z-10"></div>
        </div>
      )}
      {userFlag && (
        <div
          className="rounded-full mb-0.5 text-white relative"
          style={{ backgroundColor: '#FB923C', padding: '0.1rem' }}>
          <div
            className="absolute w-0 h-0 bottom-0 right-1/2"
            style={{
              transform: 'translate(50%,90%)',
              borderStyle: 'solid',
              borderWidth: '8px 2px 0 2px',
              borderColor: '#FB923C transparent transparent transparent',
            }}></div>
          <div className="icon-person text-sm z-10"></div>
        </div>
      )}
    </>
  );
};

const Tooltip = ({ children }: { children: React.ReactNodeArray }) => {
  return (
    <>
      <div
        style={{
          display: 'grid',
          gap: '2px',
          gridAutoRows: 'min-content',
          gridAutoFlow: 'row',
        }}>
        {children}
      </div>
    </>
  );
};

const TooltipItem = ({
  asset,
  name,
  value,
  type,
}: {
  asset: number;
  name: string;
  value: number;
  type: number;
}) => {
  const num = type === 1 ? 100 : type === 2 ? 500 : type === 3 ? 1000 : 5000;
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'min-content min-content min-content',
          placeItems: 'center start',
          gap: '2px',
        }}>
        <span style={{ fontSize: '10px', gridColumn: 2 }}>保有資産:</span>
        <span
          style={{
            fontWeight: 'bold',
            fontSize: '10px',
            gridColumn: 3,
          }}>
          {asset === 50000
            ? `${commaFormat(asset)}万円～`
            : `${commaFormat(asset)}～${commaFormat(asset + num)}万円`}
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'min-content min-content min-content',
          placeItems: 'center start',
          gap: '2px',
        }}>
        <span style={{ fontSize: '10px', gridColumn: 2 }}>{name}:</span>
        <span
          style={{
            fontWeight: 'bold',
            fontSize: '10px',
            gridColumn: 3,
          }}>
          {formatNumber(value, { numberOfDecimal: 3 })}%
        </span>
      </div>
    </>
  );
};
